import React, { useEffect, useRef, useState } from 'react';
import { Car, Calendar, Flag, Briefcase, Package, Megaphone, Sparkles } from 'lucide-react';
import { gsap } from '../lib/gsap';

const services = [
  {
    id: 1,
    title: 'Car Shoots',
    desc: 'High-end photo and video sessions for private owners, supercars, and custom builds.',
    icon: Car,
  },
  {
    id: 2,
    title: 'Car Events & Shows',
    desc: 'Full media coverage for automotive festivals and expos, delivering fast content for social media.',
    icon: Calendar,
  },
  {
    id: 3,
    title: 'Car & Bike Races',
    desc: 'High-speed trackside media and cinematic action clips for race teams, drivers, and sponsors.',
    icon: Flag,
  },
  {
    id: 4,
    title: 'Corporate Events',
    desc: 'Professional visuals for dealership launches, VIP brand experiences, and corporate milestones.',
    icon: Briefcase,
  },
  {
    id: 5,
    title: 'Product & Brand Shoots',
    desc: 'Commercial photography and lifestyle videos for automotive parts and apparel brands.',
    icon: Package,
  },
  {
    id: 6,
    title: 'Commercial Shoots',
    desc: "High-production campaigns and direct ads designed to elevate your brand's presence.",
    icon: Megaphone,
  },
  {
    id: 7,
    title: 'Custom Projects',
    desc: "Got something different. Let's talk.",
    icon: Sparkles,
  },
];

export const Services = () => {
  const sectionRef = useRef(null);
  const carouselRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const prevIndexRef = useRef(0);

  // Auto-rotate services every 3 seconds
  useEffect(() => {
    if (!isVisible) return;

    const interval = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % services.length);
    }, 3000);

    return () => clearInterval(interval);
  }, [isVisible]);

  // Animate card change - BLUR SPEED IN → CENTER → SLIDE LEFT with 3D stretch
  useEffect(() => {
    if (!carouselRef.current) return;

    const cards = carouselRef.current.querySelectorAll('[data-service-card]');
    const prevIndex = prevIndexRef.current;

    cards.forEach((card, index) => {
      gsap.killTweensOf(card);

      if (index === activeIndex) {
        // NEW CARD - Speed blur entrance from right to center
        gsap.set(card, { 
          visibility: 'visible', 
          zIndex: 10,
          position: 'absolute',
          left: '50%',
          transform: 'translateX(-50%)'
        });
        gsap.fromTo(card,
          { 
            opacity: 0, 
            x: 300,
            scaleX: 1.3,
            scaleY: 0.9,
            filter: 'blur(20px)',
            rotateY: 30
          },
          { 
            opacity: 1, 
            x: 0,
            scaleX: 1,
            scaleY: 1,
            filter: 'blur(0px)',
            rotateY: 0,
            duration: 0.7,
            ease: 'power3.out'
          }
        );
      } else if (index === prevIndex && prevIndex !== activeIndex) {
        // OLD CARD - Slide LEFT with 3D stretch motion blur
        gsap.to(card, {
          opacity: 0,
          x: -350,
          scaleX: 1.4,
          scaleY: 0.85,
          filter: 'blur(25px)',
          rotateY: -35,
          duration: 0.6,
          ease: 'power2.in',
          onComplete: () => {
            gsap.set(card, { 
              visibility: 'hidden', 
              zIndex: 1,
              x: 0,
              scaleX: 1,
              scaleY: 1,
              filter: 'blur(0px)',
              rotateY: 0
            });
          }
        });
      } else {
        // Other cards - ensure hidden
        gsap.set(card, { 
          visibility: 'hidden', 
          zIndex: 1, 
          opacity: 0 
        });
      }
    });

    prevIndexRef.current = activeIndex;
  }, [activeIndex]);

  // Intersection observer
  useEffect(() => {
    const section = sectionRef.current;
    
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          setIsVisible(entry.isIntersecting);
        });
      },
      { threshold: 0.2 }
    );

    if (section) observer.observe(section);

    return () => {
      if (section) observer.unobserve(section);
    };
  }, []);

  return (
    <section 
      id="services" 
      ref={sectionRef} 
      className="py-20 md:py-28 relative overflow-hidden"
      style={{ 
        background: 'linear-gradient(180deg, #1a0800 0%, #2d1208 30%, #3d1a0a 50%, #2d1208 70%, #1a0800 100%)'
      }}
      data-testid="services-section"
    >
      {/* Bold gradient accents */}
      <div 
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse at 0% 50%, rgba(249, 115, 22, 0.2) 0%, transparent 40%), radial-gradient(ellipse at 100% 50%, rgba(251, 146, 60, 0.15) 0%, transparent 40%)'
        }}
      />

      <div className="max-w-6xl mx-auto px-5 sm:px-8 relative z-10">
        {/* Heading */}
        <div className="text-center mb-16 md:mb-20">
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4">
            <span className="text-white">What We </span>
            <span className="bg-gradient-to-r from-orange-400 via-amber-400 to-orange-500 bg-clip-text text-transparent">Do</span>
          </h2>
          <p className="text-gray-400 text-base md:text-lg max-w-lg mx-auto">
            Full-Service Media Production. From concept to final cut.
          </p>
          <div className="w-20 h-1 bg-gradient-to-r from-orange-500 to-amber-500 mx-auto mt-6 rounded-full" />
        </div>

        {/* 3D Carousel Container */}
        <div 
          ref={carouselRef}
          className="relative h-[280px] md:h-[320px] mx-auto max-w-lg"
          style={{ perspective: '1200px' }}
        >
          {services.map((service, index) => {
            const Icon = service.icon;
            return (
              <div
                key={service.id}
                data-service-card={index}
                data-testid={`service-card-${service.id}`}
                className="absolute left-1/2 top-0 w-full max-w-md p-8 rounded-2xl border border-orange-500/20 bg-gradient-to-br from-neutral-900/90 via-neutral-900/70 to-neutral-800/50"
                style={{
                  transform: 'translateX(-50%)',
                  backdropFilter: 'blur(16px)',
                  WebkitBackdropFilter: 'blur(16px)',
                  transformStyle: 'preserve-3d',
                  opacity: index === 0 ? 1 : 0,
                  visibility: index === 0 ? 'visible' : 'hidden',
                  zIndex: index === 0 ? 10 : 1,
                }}
              >
                {/* Icon */}
                <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-amber-500 rounded-xl flex items-center justify-center mb-6 shadow-lg shadow-orange-500/30">
                  <Icon className="w-8 h-8 text-white" />
                </div>

                {/* Title */}
                <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">
                  {service.title}
                </h3>

                {/* Description */}
                <p className="text-gray-300 text-base md:text-lg leading-relaxed">
                  {service.desc}
                </p>

                {/* Service number */}
                <div className="absolute top-6 right-6">
                  <span className="text-5xl font-bold text-orange-500/10">
                    0{service.id}
                  </span>
                </div>
              </div>
            );
          })}
        </div>

        {/* Progress indicators */}
        <div className="flex justify-center gap-2 mt-10">
          {services.map((_, index) => (
            <button
              key={index}
              onClick={() => setActiveIndex(index)}
              className={`h-1.5 rounded-full transition-all duration-500 ${
                index === activeIndex 
                  ? 'w-8 bg-gradient-to-r from-orange-400 to-amber-500' 
                  : 'w-1.5 bg-white/15 hover:bg-white/30'
              }`}
              aria-label={`Go to service ${index + 1}`}
            />
          ))}
        </div>
      </div>

      {/* Mobile: Grid Fallback */}
      <div className="md:hidden px-5 mt-12">
        <div className="grid grid-cols-2 gap-3">
          {services.map((service) => {
            const Icon = service.icon;
            return (
              <div
                key={`mobile-${service.id}`}
                className="p-4 rounded-xl border border-white/5 bg-neutral-900/40"
                style={{
                  backdropFilter: 'blur(8px)',
                  WebkitBackdropFilter: 'blur(8px)',
                }}
              >
                <div className="w-10 h-10 bg-orange-500/10 rounded-lg flex items-center justify-center mb-3">
                  <Icon className="w-5 h-5 text-orange-400" />
                </div>
                <h3 className="text-sm font-semibold text-white mb-1">{service.title}</h3>
                <p className="text-gray-400 text-xs leading-relaxed line-clamp-2">{service.desc}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
